"use client"
import React, { Fragment, useEffect, useRef, useState, useMemo } from "react"
import axios from "axios"
import clsx from "clsx"
import Lucide from "@/base-components/Lucide"
import Button from "@/base-components/Button"
import Table from "@/base-components/ValueBetsGapTable"
import { readableDate, timeAgo } from "@/utils/helper"
import { ArrowDownUp, SlidersHorizontal } from "lucide-react"
import MobileLoader from "../Loaders/MobileLoader"
import { useDictionary } from "@/libs/DictionaryProvider"
import { useUser } from "@auth0/nextjs-auth0/client"
import FeatureAd from "../FeatureAd"
import DesktopLoader from "../Loaders/DesktopLoader"
import Loader from "@/base-components/Loader"
import FetchingError from "@/components/FetchingError"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { mockValueBet, roles, truncate } from "@/utils/constants"
import Menu from "@/base-components/Menu"
import Link from "next/link"
import Disclosure from "@/base-components/ValueBetDisclosure"
import Panel from "../Panel"
import { useQueryClient } from "@tanstack/react-query"
import { useWebSocket } from "@/hooks/useWebSocket"
import { useHiddenBets } from "@/hooks/useHiddenBets"
import { useUserProfile } from "@/hooks/useUserProfile"
import displayOdds from "@/utils/displayOdds"
import Panel3 from "../Panel3"
import { toast } from "sonner"
import fuzzysort from "fuzzysort"
import { useSimilarEnough } from "@/hooks/useSimilarEnough"
import ValueMobileDisclosure from "@/base-components/ValueMobileDisclosure"
import { useMatchesPlaced } from "@/hooks/useMatchesPlaced"
import Tab from "@/base-components/Tab"
import Blur from "../Blur"
import _ from "lodash"
import BlurMobile from "../BlurMobile"
import { applyFilters } from "@/utils/applyFilters"

const BetFilter = dynamic(() => import("@/components/BetFilter"))
const HideBet = dynamic(() => import("@/components/HideBet"))
const AddBetToTracker = dynamic(() => import("@/components/AddBetToTracker"))
const PanelMobile = dynamic(() => import("../PanelMobile"))

// const BOOKIE_COUNTRIES = {
//   "1xBet": ["CMR", "NGA"],
//   SportyBet: ["NGA"],
//   Bet365: ["CAN"],
//   Pinnacle: ["CAN", "NGA"],
// }

const EvTable = ({ lang }) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const dictionary = useDictionary()
  const t = dictionary.tool.positive_ev.table
  const sortOptions = useMemo(
    () => [
      {
        name: t.sort.last_modified,
        index: "LastModifiedIndex",
        order: false,
        icon: "Replace",
        selected: true,
      },
      {
        name: t.sort.bet_age,
        index: "BetAgeIndex",
        order: false,
        icon: "TimerReset",
        selected: false,
      },
      {
        name: t.sort.event_date,
        index: null,
        order: false,
        icon: "Calendar",
        selected: false,
      },
      {
        name: t.sort.expected_value,
        index: "EvIndex",
        order: false,
        icon: "CircleDollarSign",
        selected: false,
      },
      {
        name: t.sort.odds,
        index: "OddsIndex",
        order: false,
        icon: "CirclePercent",
        selected: false,
      },
    ],
    [
      t.sort.bet_age,
      t.sort.event_date,
      t.sort.expected_value,
      t.sort.last_modified,
      t.sort.odds,
    ],
  )
  const { data: profile } = useUserProfile()
  const [sortKeys, setSortKeys] = useState(sortOptions)
  const [sortBy, setSortBy] = useState(sortOptions[0].name)
  const [lsi, setLsi] = useState(sortOptions[0].index)
  const { user, isLoading } = useUser()
  const [showFilter, setShowFilter] = useState(false)
  const [errorFetchingBets, setErrorFetchingBets] = useState(false)
  const [showAddBetToTracker, setShowAddBetToTracker] = useState(false)
  const [bet, setBet] = useState<GeneralObject>(undefined)
  const [hideBetModal, setHideBetModal] = useState(false)
  const socketNotifButton = useRef(null)
  const [newBets, setNewBets] = useState([])
  const isClassic = _.includes(user?.[roles], "classic")

  // --- Search state ---
  const [searchInput, setSearchInput] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const { similarEnough } = useSimilarEnough()

  const {
    data,
    isLoading: loading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error,
  } = useInfiniteQuery({
    queryKey: ["evBets", sortBy],
    queryFn: async ({ pageParam = null }) => {
      const res = await axios.get(`/api/positive-ev`, {
        params: {
          cursor: JSON.stringify(pageParam),
          lsi: lsi,
        },
      })
      return res.data
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  })

  const { data: preferences } = useQuery({
    queryKey: ["preferences", user?.sub],
    queryFn: async () => {
      const res = await axios.get(`/api/preferences`)
      return res.data
    },
    enabled: !!user,
  })

  const {
    data: automationKey,
    isLoading: isAutomationKeyLoading,
    isFetching: isAutomationKeyFetching,
    refetch: refetchAutomationKey,
  } = useQuery({
    queryKey: ["automation-key", user?.sub],
    queryFn: async () => {
      const res = await axios.get(`/api/trade-wallet/automation-key`)
      return res.data
    },
    enabled: !!user,
  })

  useEffect(() => {
    if (showFilter && user?.sub) void refetchAutomationKey()
  }, [refetchAutomationKey, showFilter, user?.sub])

  // Helper function to display bookmaker name (replace 1xBet with selected clone if applicable)
  const getDisplayBookmaker = bookmaker => {
    if (
      bookmaker === "1xBet" &&
      preferences?.selected1xBetClone &&
      _.includes(preferences?.bookmakers, "1xBet")
    ) {
      return preferences.selected1xBetClone
    }
    return bookmaker
  }

  // Helper function to modify URL for 1xBet clones
  const getBookmakerUrl = (url, bookmaker) => {
    if (
      bookmaker === "1xBet" &&
      preferences?.selected1xBetClone &&
      _.includes(preferences?.bookmakers, "1xBet")
    ) {
      const cloneName = preferences.selected1xBetClone.toLowerCase()
      // Replace the 1xbet domain with the clone domain, preserving the TLD
      const modifiedUrl = url.replace(
        /(https?:\/\/)(www\.)?1xbet\.[a-z]+/gi,
        `$1$2${cloneName}.com`,
      )
      // console.log("URL Modification:", {
      //   original: url,
      //   modified: modifiedUrl,
      //   clone: preferences.selected1xBetClone,
      //   cloneName,
      //   bookmaker,
      // })
      return modifiedUrl
    }
    return url
  }

  const { data: promoBets } = useQuery({
    queryKey: ["Promo Bets"],
    queryFn: async () => {
      const res = await axios.get(`/api/positive-ev/promo-bets`)
      return res.data
    },
    enabled: !!user && !isClassic,
    // staleTime: 1000 * 60 * 5,
  })

  const { hiddenBets, hideBet, isHidden } = useHiddenBets()
  const { matchPlaced, matchIsPlaced } = useMatchesPlaced()
  const evBets = useMemo(
    () =>
      data?.pages
        .flatMap(page => page.items)
        .filter(
          bet =>
            !isHidden(
              `${bet.bet_type}_${bet.match_id}_${bet.market}_${bet.bookmaker1}_${bet.bookmaker2}`,
            ) &&
            !isHidden(
              `${bet.bet_type}_${bet.match_id}_${bet.bookmaker1}_${bet.bookmaker2}`,
            ) &&
            !isHidden(`${bet.bet_type}_${bet.match_id}_${bet.market}`) &&
            new Date().toISOString() < bet.match_date &&
            applyFilters(preferences, bet),
        ) ?? [],
    [data?.pages, isHidden, preferences],
  )

  // const evBets = data?.pages.flatMap(page => page.items) ?? []
  const { data: bookies } = useQuery({
    queryKey: ["bookies"],
    queryFn: async () => {
      const res = await axios.get(`/api/bookies`, {})
      return res.data
    },
    enabled: !!user,
  })

  const { data: sports } = useQuery({
    queryKey: ["sports"],
    queryFn: async () => {
      const res = await axios.get(`/api/sports`, {})
      return res.data
    },
    enabled: !!user,
  })

  const { data: positiveEvOptions } = useQuery({
    queryKey: ["positive-ev-filter-options"],
    queryFn: async () => {
      const res = await axios.get(`/api/positive-ev/options`)
      return res.data
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  })

  const { data: betData, isConnected, connect, disconnect } = useWebSocket()

  useEffect(() => {
    setSortKeys(sortOptions)
    setSortBy(sortOptions[0].name)
    setLsi(sortOptions[0].index)
  }, [sortOptions])

  useEffect(() => {
    if (user && !isLoading && isClassic) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [connect, disconnect, user, isLoading, isClassic])

  useEffect(() => {
    try {
      const bet = JSON.parse(betData)

      if (
        bet["bet_type"] === "value_bet" &&
        !isHidden(
          `${bet.bet_type}_${bet.match_id}_${bet.market}_${bet.bookmaker1}_${bet.bookmaker2}`,
        ) &&
        !isHidden(
          `${bet.bet_type}_${bet.match_id}_${bet.bookmaker1}_${bet.bookmaker2}`,
        ) &&
        !isHidden(`${bet.bet_type}_${bet.match_id}_${bet.market}`) &&
        new Date().toISOString() < bet.match_date &&
        applyFilters(preferences, bet)
        // &&
        // !evBets.find(el => el.sk === bet.sk)
      ) {
        setNewBets(previous => [bet, ...previous])
        toast(
          <div className="flex flex-col rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-start">
                <div className="ml-3 flex-1">
                  <p className=" font-medium text-sm text-gray-500">
                    {t.toasts.new_realtime_bets}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex rounded-lg  border-slate-200/60">
              <button
                onClick={() => {
                  toast.dismiss()

                  queryClient.setQueryData(
                    ["evBets", sortBy],
                    (oldData: any) => {
                      if (!oldData) return oldData

                      const pageSize = oldData.pages[0]?.items.length ?? 10

                      const allItems = oldData.pages.flatMap(
                        (p: any) => p.items,
                      )

                      const withNew = [bet, ...allItems]

                      const uniqueItems = Array.from(
                        new Map(
                          withNew.map(item => [item.sort_key, item]),
                        ).values(),
                      )

                      const newPages = []
                      for (let i = 0; i < uniqueItems.length; i += pageSize) {
                        newPages.push({
                          items: uniqueItems.slice(i, i + pageSize),
                        })
                      }

                      return { ...oldData, pages: newPages }
                    },
                  )
                  setNewBets([])
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  })
                  toast.success(t.toasts.refreshed_top)
                }}
                className="w-full border rounded-b-lg  border-slate-200/60 p-2 px-4 flex items-center justify-center text-sm font-medium text-blue-500 hover:bg-gray-50 focus:outline-none"
              >
                {t.actions.refresh}
              </button>
              <button
                onClick={() => toast.dismiss()}
                className="w-full border p-2 px-4 rounded-b-lg  border-l-0 flex items-center justify-center text-sm font-medium text-red-500 hover:bg-gray-50 focus:outline-none"
              >
                {t.actions.dismiss}
              </button>
            </div>
          </div>,
          {
            duration: Infinity,
            id: "custom-toast",
            className:
              "!bg-transparent !border-0 w-fit flex justify-center items-center !p-0  !z-[99999] ",
          },
        )
      }
    } catch (error) {}
  }, [
    betData,
    // isHidden,
    // preferences,
    // queryClient,
    // sortBy,
    // t.actions.dismiss,
    // t.actions.refresh,
    // t.toasts.new_realtime_bets,
    // t.toasts.refreshed_top,
  ])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        disconnect()
      } else {
        if (user && !isLoading && isClassic) {
          connect()
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [connect, disconnect, user, isLoading, isClassic])

  const { ref, inView } = useInView({
    threshold: 1,
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    toast.dismiss()
    return () => {
      toast.dismiss()
    }
  }, [])

  // --- Filtered list for rendering ---
  const displayBets = useMemo(() => {
    if (!evBets || !user?.countryCode) return []

    return (
      evBets

        // --- Search filter ---
        .filter((b: any) => {
          if (!searchTerm) return true

          const eventStr = b?.event || ""
          const altA = b?.teamA ? String(b.teamA) : ""
          const altB = b?.teamB ? String(b.teamB) : ""
          const altCombined = altA && altB ? `${altA} vs ${altB}` : ""

          return (
            similarEnough(eventStr, searchTerm) ||
            (altCombined && similarEnough(altCombined, searchTerm))
          )
        })
    )
  }, [evBets, searchTerm, user?.countryCode, similarEnough])

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchTerm(searchInput.trim())
    } else if (e.key === "Escape") {
      setSearchInput("")
      setSearchTerm("")
    }
  }

  const handleHide = key => {
    try {
      hideBet(key)
      toast.dismiss()

      toast.success(t.toasts.bets_hidden)
    } catch (error) {}
  }

  const handleSort = e => {
    const { id } = e.target
    setSortBy(sortKeys[id].name)
    setLsi(sortKeys[id].index)
    let temp = sortKeys.map((key, i) => {
      return { ...key, selected: i === Number(id) ? true : false }
    })
    setSortKeys(temp)
  }

  const applyChanges = async body => {
    try {
      toast.loading(t.toasts.saving_wait)
      await axios.post(`/api/preferences`, body)
      toast.dismiss()
      toast.success(t.toasts.saved)
      queryClient.invalidateQueries({ queryKey: ["preferences", user.sub] })
      queryClient.invalidateQueries({ queryKey: ["evBets"] })
      return true
    } catch (error) {
      toast.dismiss()
      toast.error(
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : t.toasts.save_failed,
      )
      return false
    }
  }

  if (isLoading) {
    return <Loader />
  }

  if (!user) {
    return <FeatureAd lang={lang} />
  }

  if (errorFetchingBets) {
    return <FetchingError />
  }

  return (
    <>
      {bookies && sports && (
        <BetFilter
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          bookies={bookies}
          sports={sports}
          scoreboards={positiveEvOptions?.scoreboards ?? []}
          markets={positiveEvOptions?.markets ?? []}
          preferences={preferences}
          hasAutomationKey={Boolean(automationKey?.hasKey)}
          isAutomationKeyLoading={
            isAutomationKeyLoading || isAutomationKeyFetching
          }
          applyChanges={applyChanges}
        />
      )}
      <button
        // onClick={() => showTaost()}
        className="hidden"
        ref={socketNotifButton}
      ></button>
      {bet && (
        <AddBetToTracker
          showAddBetToTracker={showAddBetToTracker}
          setShowAddBetToTracker={setShowAddBetToTracker}
          bet={bet}
          hideBet={hideBet}
          matchPlaced={matchPlaced}
          bookies={bookies}
        />
      )}{" "}
      {bet && (
        <HideBet
          hideBetModal={hideBetModal}
          setHideBetModal={setHideBetModal}
          bet={bet}
          handleHide={handleHide}
        />
      )}{" "}
      <Tab.Group>
        <div className="my-2">
          <Tab.List
            variant="boxed-tabs"
            className={clsx([
              "z-[99] flex flex-row no-scrollbar h-fit border rounded-md px-1 py-0  border-dashed lg:w-[50%] lg:items-start  my-2 items-center overflow-x-auto  ",
            ])}
          >
            <Tab className={clsx(["!min-h-full"])}>
              <Tab.Button
                as="button"
                className={clsx(["text-sm w-full !h-full py-0 px-1 "])}
              >
                {t.tabs.pre_match}
              </Tab.Button>
            </Tab>
            <Tab className={clsx(["!min-h-full"])}>
              <Tab.Button
                as="button"
                className={clsx(["text-sm w-full !h-full py-0 px-1 "])}
              >
                {t.tabs.live}
              </Tab.Button>
            </Tab>
          </Tab.List>
        </div>
        <div className="lg:flex  gap-2 my-4 !py-0  w-full ">
          <div className="relative flex-1 flex  mt-3 sm:ml-auto sm:mt-0 text-slate-500">
            <Lucide
              icon="Search"
              className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3"
            />
            <input
              type="text"
              placeholder={t.search_placeholder}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="bg-dark-7  p-2 px-10 border border-white/[0.12] focus:border-purple outline-none w-full rounded-lg text-white"
            />
          </div>
          <div
            className={clsx([
              "lg:mt-0 mt-2 lg:w-[50%]  w-full  flex justify-end",
              !isClassic && "pointer-events-none",
            ])}
          >
            <Menu>
              <Menu.Button as={Fragment}>
                <Button className="mr-2 border-white/[0.12]  bg-dark-7">
                  <ArrowDownUp className="mr-2" />
                  {t.sort_by}
                </Button>
              </Menu.Button>
              <Menu.Items className="mt-2 border border-white/[0.12] bg-dark-7  w-50">
                {sortKeys.map((el, index) => {
                  return (
                    <Menu.Item
                      key={el.index ?? String(index)}
                      onClick={handleSort}
                      id={index}
                      className={clsx([
                        "hover:bg-white/5",
                        el.selected && "text-purple",
                      ])}
                    >
                      <Lucide
                        icon={el.icon}
                        className={clsx(["w-4 h-4 mr-2"])}
                      />
                      {el.name}
                    </Menu.Item>
                  )
                })}
              </Menu.Items>
            </Menu>
            <Menu>
              <Menu.Button as={Fragment}>
                <Button
                  onClick={() => setShowFilter(true)}
                  className="mr-2 border-white/[0.12]  bg-dark-7"
                >
                  <SlidersHorizontal className="mr-2" />
                  {t.filter}
                </Button>
              </Menu.Button>
            </Menu>
          </div>
        </div>
        <Tab.Panels className=" w-full mx:auto mt-2">
          <Tab.Panel>
            {/* constructing table with display grid */}
            <div className="hidden lg:block overflow-auto overflow-x-scroll intro-y lg:overflow-visible mb-4 !min-w-[1100px]">
              <div className="grid w-full grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)_minmax(0,1.5fr)_minmax(0,2.3fr)_minmax(0,0.5fr)_minmax(0,1.2fr)_minmax(0,1.5fr)] mb-4 ">
                {/* headers */}
                <div className="text-center font-medium px-5 py-3 ">
                  {t.headers.ev}
                </div>
                <div className="text-center font-medium px-5 py-3 ">
                  {t.headers.event}
                </div>
                <div className="text-center font-medium px-5 py-3 ">
                  {t.headers.market}
                </div>
                <div className="text-center font-medium px-5 py-3 ">
                  {t.headers.bet}
                </div>
                <div className="text-left font-medium px-0 py-3 ">
                  {t.headers.odds}
                </div>
                <div className="text-left font-medium px-2 py-3 ">
                  {t.headers.sportsbook}
                </div>
                <div className="text-center font-medium px-5 py-3 "></div>
              </div>
              <div className="flex w-full flex-col gap-2 ">
                {!loading
                  ? displayBets?.map((bet, key) => {
                      const events =
                        bet.market_info.market_odds["OddsFantasy"]?.length
                      return (
                        <Disclosure
                          key={key}
                          className={clsx([
                            "bg-dark-8 rounded-md w-full",
                            matchIsPlaced(bet.match_id) &&
                              "border border-purple",
                          ])}
                        >
                          <Disclosure.Button className="block w-full text-left ">
                            <div className="grid w-full  grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)_minmax(0,1.5fr)_minmax(0,2.3fr)_minmax(0,0.5fr)_minmax(0,1.2fr)_minmax(0,1.5fr)] bg-dark-8 rounded-md hover:cursor-pointer hover:bg-white/5 ">
                              <div className="px-3 py-3  flex flex-col justify-center items-center border-b-0">
                                <div className="font-medium text-sm whitespace-wrap">
                                  {`${bet?.ev}%`}
                                </div>
                                <div className="text-slate-500 text-xs whitespace-nowrap mt-0.5">
                                  {`${t.updated_prefix} ${timeAgo(bet?.last_modified)}`}
                                </div>
                              </div>
                              <div className="px-3 py-3 flex flex-col justify-center  border-b-0">
                                <div className="text-slate-500 text-xs whitespace-nowrap mt-0.5">
                                  {`${readableDate(bet?.match_date, user.timezone)}`}
                                </div>
                                <div className="font-medium text-sm whitespace-wrap ">
                                  {bet.event || `${bet.teamA} vs ${bet.teamB}`}
                                </div>
                                <div className="text-slate-500 text-xs whitespace-nowrap mt-0.5">
                                  {bet.sport_name} | {truncate(bet.country, 15)}{" "}
                                  | {truncate(bet.competition, 15)}
                                </div>
                              </div>

                              <div className="px-3 py-3  text-center flex flex-col justify-center items-center text-sm  border-b-0">
                                <div className="font-medium text-sm whitespace-wrap">
                                  {bet.market}
                                </div>
                                <div className="text-slate-500 text-xs whitespace-nowrap mt-0.5">
                                  {bet.scoreboard}
                                </div>
                              </div>
                              <div className="px-3 py-3  text-center text-sm  border-b-0 flex flex-col justify-center items-center">
                                {bet.bet}
                              </div>
                              <div className="px-3 py-3  text-center text-sm  border-b-0 flex flex-col justify-center items-center">
                                {displayOdds(profile?.oddsFormat, bet.odds)}
                              </div>

                              <div className="px-3 py-3  text-center flex flex-col justify-center items-center  border-b-0">
                                <div>{getDisplayBookmaker(bet.bookmaker2)}</div>
                              </div>
                              <div className="px-3 py-3   flex flex-col justify-center items-center  border-b-0 py-0 relative before:block before:w-px before:h-8 before:bg-white/[0.12] before:absolute before:left-0 before:inset-y-0 before:my-auto before:dark:bg-darkmode-400">
                                <div className="flex items-center justify-between">
                                  <Link
                                    href={getBookmakerUrl(
                                      bet.url2,
                                      bet.bookmaker2,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => {
                                      e.stopPropagation()
                                    }}
                                  >
                                    <Button
                                      as="div"
                                      title={t.actions.view_on_bookmaker}
                                      className="justify-between  items-center bg-slate-100/10 transition duration-200 border-none inline-flex  font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed text-xs py-[.45rem] px-1 rounded-md text-md !shadow-none whitespace-nowrap"
                                    >
                                      <span className="mr-1 text-purple-light">
                                        {t.actions.bet}
                                      </span>
                                      <Lucide
                                        className="text-purple-light mr-0"
                                        icon="ExternalLink"
                                      />
                                    </Button>
                                  </Link>
                                  <Button
                                    as="div"
                                    onClick={e => {
                                      e.stopPropagation()
                                      setBet(bet)
                                      setHideBetModal(true)
                                    }}
                                    title={t.actions.hide}
                                    className=" justify-between items-center hover:bg-slate-100/10 transition duration-200 border-none inline-flex  font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed text-xs py-[.45rem] px-1 rounded-md text-md !shadow-none whitespace-nowrap"
                                  >
                                    <Lucide
                                      className="text-purple mr-0"
                                      icon="EyeOff"
                                    />
                                  </Button>{" "}
                                  <Button
                                    as="div"
                                    onClick={e => {
                                      e.stopPropagation()
                                      setBet(bet)
                                      setShowAddBetToTracker(true)
                                    }}
                                    title={t.actions.add_to_tracker}
                                    className=" justify-between items-center hover:bg-slate-100/10 transition duration-200 border-none inline-flex  font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed text-xs py-[.45rem] px-1 rounded-md text-md !shadow-none whitespace-nowrap"
                                  >
                                    <Lucide
                                      className="text-purple-dark mr-0"
                                      icon="CirclePlus"
                                    />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Disclosure.Button>
                          <Disclosure.Panel className="text-xs flex justify-center items-center p-2">
                            {events === 2 ? (
                              <Panel bet={bet} />
                            ) : (
                              <Panel3 bet={bet} />
                            )}
                          </Disclosure.Panel>
                        </Disclosure>
                      )
                    })
                  : [...Array(10)].map((_, key) => {
                      return <DesktopLoader key={key} />
                    })}
                {!isClassic
                  ? promoBets?.items?.map((bet, key) => {
                      return <Blur bet={bet} key={key} />
                    })
                  : null}
              </div>
            </div>
            {/* New Table */}
            {/* Mobile view */}
            <div className="lg:hidden flex flex-col gap-4 mb-3">
              {!loading
                ? displayBets.map((bet, key) => {
                    return (
                      <div
                        key={key}
                        className={clsx([
                          "bg-dark-8 hover:cursor-pointer rounded-lg p-3 py-2 ",
                          matchIsPlaced(bet.match_id)
                            ? "border border-purple"
                            : "gradient-box",
                        ])}
                      >
                        <div className="grid grid-cols-12 gap-1 border-b  border-white/[0.12] pb-2 my-2">
                          <div className="col-span-12 font-semibold text-sm">
                            {bet.event || `${bet?.teamA} vs ${bet?.teamB}`}
                          </div>

                          <div className="col-span-6 !text-xs text-slate-600 whitespace-nowrap overflow-hidden text-ellipsis">
                            {bet.sport_name} | {truncate(bet.country, 10)} |{" "}
                            {truncate(bet.competition, 10)}
                          </div>
                          <div className="col-span-6 text-right text-xs">
                            {`${readableDate(bet.match_date, user.timezone)}`}
                          </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2">
                          <span className="flex flex-col text-sm font-regular mb-3 mt-1 col-span-3 ">
                            <span className="font-semibold mr-2 !leading-5">
                              {t.mobile.ev}
                            </span>
                            <span className="!text-xs text-slate-600">
                              {" "}
                              {`${bet.ev}%`}
                            </span>
                          </span>{" "}
                          <span className="flex flex-col text-sm font-regular mb-3 mt-1 col-span-6">
                            <span className="font-semibold mr-2 !leading-5 text-center">
                              {t.mobile.market}
                            </span>
                            <span className="!text-xs text-slate-600 text-center">
                              {bet.market} | {bet.scoreboard}
                            </span>
                          </span>{" "}
                          <span className="flex flex-col text-sm font-regular mb-3 mt-1 col-span-3 ">
                            <span className="font-semibold  !leading-5 text-right">
                              {t.mobile.odds}
                            </span>
                            <span className="!text-xs text-slate-600 text-right">
                              {bet.odds}
                            </span>
                          </span>{" "}
                          <span className="flex flex-col text-sm font-regular mb-3 mt-1 col-span-3 ">
                            <span className="font-semibold  !leading-5">
                              {t.mobile.sportsbook}
                            </span>
                            <span className="flex flex-col  justify-center  !text-xs text-slate-600">
                              <span>{getDisplayBookmaker(bet.bookmaker2)}</span>
                              {/* <div className="flex justify-center">
                          <img
                            className=" inline w-[30px] h-[30px] rounded-sm "
                            // src={`/images/bookmaker_logos/${el.bookmaker}.png`}
                            src={`https://oddsfantasyfrontendfiles.s3.amazonaws.com/bookmaker_logos/${getDisplayBookmaker(bet.bookmaker2)}.png`}
                            alt=""
                          />
                        </div> */}
                            </span>
                          </span>{" "}
                          <span className="flex flex-col text-sm font-regular mb-3 mt-1 col-span-6 text-center">
                            <span className="font-semibold mr-2 !leading-5">
                              {t.mobile.bet}
                            </span>
                            <span className="!text-xs text-slate-600">
                              {bet.bet}
                            </span>
                          </span>{" "}
                          <span className="flex flex-col text-sm font-regular mb-3 mt-1 col-span-3 text-right">
                            <span className="font-semibold !leading-5">
                              {t.mobile.updated}
                            </span>
                            <span className="!text-xs text-slate-600">{`${timeAgo(bet.last_modified)}`}</span>
                          </span>{" "}
                        </div>
                        <div className="w-full flex justify-center py-0 gap-2 border-t border-white/[0.12]">
                          <ValueMobileDisclosure className="w-full">
                            <ValueMobileDisclosure.Panel className="w-full px-2">
                              <div className="flex justify-center">
                                <Link
                                  href={getBookmakerUrl(
                                    bet.url2,
                                    bet.bookmaker2,
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e => {
                                    e.stopPropagation()
                                  }}
                                >
                                  <Button
                                    as="div"
                                    title={t.actions.view_on_bookmaker}
                                    className="justify-between  items-center bg-slate-100/10 transition duration-200 border-none inline-flex  font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed text-xs py-[.45rem] px-1 rounded-md text-md !shadow-none whitespace-nowrap"
                                  >
                                    <span className="mr-1 text-purple-light">
                                      {t.actions.bet}
                                    </span>
                                    <Lucide
                                      className="text-purple-light mr-0"
                                      icon="ExternalLink"
                                    />
                                  </Button>
                                </Link>
                                <Button
                                  onClick={e => {
                                    e.stopPropagation()
                                    setBet(bet)
                                    setHideBetModal(true)
                                  }}
                                  title={t.actions.hide}
                                  className=" justify-between items-center hover:bg-slate-100/10 transition duration-200 border-none inline-flex  font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed text-xs py-[.45rem] px-1 rounded-md text-md !shadow-none whitespace-nowrap"
                                >
                                  <Lucide
                                    className="text-purple mr-0"
                                    icon="EyeOff"
                                  />
                                </Button>{" "}
                                <Button
                                  onClick={() => {
                                    setBet(bet)
                                    setShowAddBetToTracker(true)
                                  }}
                                  title={t.actions.add_to_tracker}
                                  className=" justify-between w-10 h-10 items-center hover:bg-slate-100/10  transition rounded-full duration-200 border-none inline-flex  font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none   [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed text-xs py-[.45rem] px-1 text-md !shadow-lg whitespace-nowrap"
                                >
                                  <Lucide
                                    className="text-purple w-6 h-6 mx-auto"
                                    icon="CirclePlus"
                                  />
                                </Button>
                              </div>
                              <div className=" !overflow-x-scroll">
                                <PanelMobile bet={bet} />
                                {/* <Panel bet={bet} /> */}
                              </div>
                            </ValueMobileDisclosure.Panel>
                            <ValueMobileDisclosure.Button
                              className="flex text-white text-center font-semibold  mt-1 p-2 cursor-pointer rounded-3xl"
                              // collasped={collasped}
                            ></ValueMobileDisclosure.Button>
                          </ValueMobileDisclosure>
                        </div>
                      </div>
                    )
                  })
                : [...Array(10)].map((_, key) => {
                    return <MobileLoader key={key} />
                  })}
              {!isClassic
                ? promoBets?.items?.map((bet, key) => {
                    return <BlurMobile bet={bet} key={key} />
                  })
                : null}
            </div>
            {isClassic ? (
              <div className="text-center w-full mx-auto py-5" ref={ref}>
                {isFetchingNextPage
                  ? t.pagination.loading_more
                  : hasNextPage
                    ? t.pagination.load_more
                    : t.pagination.no_more_results}
              </div>
            ) : (
              <div className="w-full flex justify-center items-center">
                <Link
                  href={`/${lang}/pricing`}
                  className="hero-button-gradient inline-flex rounded-lg mb-3 mx-auto py-3 px-7 text-white font-medium ease-in duration-300 hover:opacity-80"
                >
                  {t.subscribe_cta}
                </Link>
              </div>
            )}
          </Tab.Panel>
          <Tab.Panel className="w-full lg:h-[300px] h-[200px] border border-white/[0.12] rounded-lg flex justify-center items-center">
            {/* <div className="w-full h-full "> */}
            <div className="font-bold text-lg">{t.coming_soon}</div>
            {/* </div> */}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </>
  )
}

export default EvTable
